import type { OutboxBackend, OutboxItem } from './taskOutbox';

/**
 * The task outbox's own IndexedDB database (B4, 2026-10-25) — separate from
 * the audit store (src/local/db.ts) on purpose: adding a store there means a
 * version upgrade, which an older tab still holding the database open would
 * block, stalling audits. Never localStorage (spec §8).
 */

const DB_NAME = 'ssp-auditplus-outbox';
const DB_VERSION = 1;
const ITEMS = 'items';
const BLOBS = 'blobs';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(ITEMS)) db.createObjectStore(ITEMS, { keyPath: 'id' });
        if (!db.objectStoreNames.contains(BLOBS)) db.createObjectStore(BLOBS);
      };
      request.onsuccess = () => {
        const db = request.result;
        // A later version (a future deploy) in another tab: let it upgrade.
        db.onversionchange = () => {
          db.close();
          dbPromise = null;
        };
        resolve(db);
      };
      request.onerror = () => {
        dbPromise = null;
        reject(request.error ?? new Error('IndexedDB open failed'));
      };
    });
  }
  return dbPromise;
}

function done<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

async function store(name: string, mode: IDBTransactionMode): Promise<IDBObjectStore> {
  const db = await openDb();
  return db.transaction(name, mode).objectStore(name);
}

export const idbOutboxBackend: OutboxBackend = {
  list: async () => done((await store(ITEMS, 'readonly')).getAll() as IDBRequest<OutboxItem[]>),
  put: async (item) => {
    await done((await store(ITEMS, 'readwrite')).put(item));
  },
  delete: async (id) => {
    await done((await store(ITEMS, 'readwrite')).delete(id));
  },
  getBlob: async (id) => done((await store(BLOBS, 'readonly')).get(id) as IDBRequest<Blob | undefined>),
  putBlob: async (id, blob) => {
    await done((await store(BLOBS, 'readwrite')).put(blob, id));
  },
  deleteBlob: async (id) => {
    await done((await store(BLOBS, 'readwrite')).delete(id));
  },
};
